/**
 * Pure decision helper for the on-reconnect drip reply-exit catch-up
 * (mirrors the campaign-stats reconcile in session-status-automaton.ts).
 *
 * On engine reconnect the automaton fetches the owner's active stop-on-reply
 * drip targets, reads each target chat's recent messages from the WA store, and
 * feeds them here. This module is intentionally free of any WA-store / RTK
 * dependency so it can be unit-tested in isolation.
 */

import { isCancelIfReceivedTrigger } from '../thunks/cancel-if-received-trigger';

export interface DripReplyTarget {
    whatsappId: string;
    /** Epoch ms — only inbound messages at/after this instant count as a reply. */
    sinceTs: number;
}

/**
 * A store message projected to the fields the decision needs. The type/content
 * fields let a WhatsApp system event (security-code change, e2e notice, revoke,
 * reaction, missed call…) be told apart from a real reply; all are optional so a
 * bare `{ fromMe, t }` still counts as a content message.
 */
export interface ScanMsg {
    fromMe: boolean;
    /** Epoch ms (normalize from the store's unix-seconds `t` before passing in). */
    t: number;
    type?: string;
    waType?: string;
    body?: string;
    caption?: string;
    mediaKey?: unknown;
    hasMedia?: boolean;
}

/**
 * Given the scan targets and, per target chat, its messages, return the
 * whatsappIds that have at least one INBOUND (!fromMe) real-reply message at/after the
 * target's sinceTs window — the recipients to exit.
 */
export function collectRepliedTargets(
    targets: DripReplyTarget[],
    messagesByChat: Record<string, ScanMsg[]>,
): string[] {
    const out: string[] = [];
    for (const t of targets) {
        const msgs = messagesByChat[t.whatsappId] ?? [];
        if (msgs.some((m) => m.t >= t.sinceTs && isCancelIfReceivedTrigger(m))) out.push(t.whatsappId);
    }
    return Array.from(new Set(out));
}
